import type { CSSProperties, FC } from 'react';
import { useEffect, useState } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { Box, Typography } from '@mui/material';

import { Error, InputNumber, Text, Tooltip } from 'ui';
import { theme } from 'ui/theme';
import palette from 'ui/theme/palette';
import { MAX_WEIGHT, MIN_WEIGHT, parseWeightEntry } from './section-weight-summary.util';

/**
 * Validation for the form variant, kept beside the inline variant's parseWeightEntry so the two
 * agree on what a valid weight is. The inline variant clamps silently; the modal reports instead,
 * because there the user is still composing the question.
 */
export const weightFieldRules = {
  validate: (data: unknown) => {
    const value = data as string | number | undefined;

    if (value === undefined || value === null || value === '') {
      return true;
    }

    const parsed = Number(value);

    if (!Number.isFinite(parsed)) {
      return 'must be a number.';
    }

    // The API stores weights as integers, so reject decimals rather than letting the save fail.
    if (!Number.isInteger(parsed)) {
      return 'must be a whole number.';
    }

    return (
      (parsed >= MIN_WEIGHT && parsed <= MAX_WEIGHT) || `must be between ${MIN_WEIGHT} and ${MAX_WEIGHT}.`
    );
  }
};

interface FormVariantProps {
  /** Bound to a React Hook Form field. Used by the field modal, where weight saves with the form. */
  variant: 'form';
  form: UseFormReturn<FieldValues>;
  name: string;
  label: string;
  descriptionText?: string;
  dataCy?: string;
}

interface InlineVariantProps {
  /** Compact input that commits on blur. Used by the builder rows, where each edit is its own save. */
  variant: 'inline';
  value: number | undefined;
  onCommit: (weight: number | undefined) => void;
  tooltip: string;
  ariaLabel: string;
  /** Set on rows that are themselves clickable, so typing doesn't trigger the row. */
  stopClickPropagation?: boolean;
  dataCy?: string;
}

export type WeightInputProps = FormVariantProps | InlineVariantProps;

const INLINE_INPUT_STYLE: CSSProperties = {
  width: '64px',
  padding: '4px 6px',
  border: `1px solid ${palette.divider}`,
  borderRadius: '4px',
  fontFamily: 'inherit',
  fontSize: '14px'
};

const InlineWeightInput: FC<InlineVariantProps> = (props) => {
  // Held locally so typing is smooth; only committed on blur, since each commit is a save.
  const [draft, setDraft] = useState(props.value?.toString() ?? '');

  useEffect(() => {
    setDraft(props.value?.toString() ?? '');
  }, [props.value]);

  const commit = () => {
    const next = parseWeightEntry(draft);

    // Show the clamped value back, so the field never keeps something the API would reject.
    if (next !== undefined && next.toString() !== draft.trim()) {
      setDraft(next.toString());
    }

    if (next === props.value) {
      return;
    }

    props.onCommit(next);
  };

  return (
    <Tooltip placement="bottom" title={props.tooltip}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Text inline content="Weight" type="body3" />
        <input
          type="number"
          min={MIN_WEIGHT}
          max={MAX_WEIGHT}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onClick={props.stopClickPropagation ? (event) => event.stopPropagation() : undefined}
          aria-label={props.ariaLabel}
          data-cy={props.dataCy}
          style={INLINE_INPUT_STYLE}
        />
        <Text inline content="%" type="body3" />
      </Box>
    </Tooltip>
  );
};

/** The one weight entry point: a labelled form field in the modal, a compact row input inline. */
export const WeightInput: FC<WeightInputProps> = (props) => {
  if (props.variant === 'form') {
    const validationError = props.form.formState.errors[props.name];

    return (
      <>
        {/* maxInteger is deliberately unused: it rejects >= its value, blocking a legitimate 100.
            onError renders in place of InputNumber's own message, so returning nothing suppresses
            it - it flags an emptied field as "must contain numbers only", which is wrong here. */}
        <InputNumber
          control={props.form.control}
          name={props.name}
          label={props.label}
          rules={weightFieldRules}
          dataCy={props.dataCy}
          isIntegerOnly
          allowNegative={false}
          onError={() => undefined}
        />
        {validationError && (
          <Error
            content={`* ${props.label} ${(validationError.message as string) ?? ''}`}
            dataCy="validation-message"
          />
        )}
        {props.descriptionText && (
          <Typography
            data-cy="description-text"
            variant="body1"
            color={theme.palette.text.primary}
            fontSize="12px"
          >
            {props.descriptionText}
          </Typography>
        )}
      </>
    );
  }

  return <InlineWeightInput {...props} />;
};
